import React, { useMemo, useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Global from '../store/global-selectors';

import PropTypes from 'prop-types';
import { FormattedMessage, defineMessages } from 'react-intl';

import { Button } from 'ui-components/components/button';
import { noop } from 'ui-components/default-props/noop';

import ReadOnlyAddress from '../components/address';
import style from './address.module.less';
import { ControlledComponentAddressForm } from './controlled-component';
import { EditAddressComponent } from './edit-address';
import UnlockAddress from '../components/address-unlocking';
import { AddressType, SHIP_TO_ADDRESS_TYPE } from './prop-types/address-type';
import { unlockAddressRequest } from '../store/entities/entities.actions';
import { emptyArray } from 'ui-components/default-props/empty';

const messages = defineMessages({
  editAddress: {
    description: 'shipTo.editAddress',
    defaultMessage: 'Edit Address',
    id: '+slKGs',
  },
  editAddressDisabled: {
    description: 'shipTo.editAddressDisabled',
    defaultMessage: 'Address cannot be edited',
    id: 'fU9DZU',
  },
  lockAddress: {
    description: 'Message indicating address is locked and will not reflect address changes made in the market place',
    defaultMessage: 'This address has been modified and will ignore future changes from the marketplace.',
    id: 'Ztjm0D',
  },
  lockButton: {
    description: 'Button that allows user to unlock address so it can receive updated address from the market place',
    defaultMessage: 'Unlock Address',
    id: '1RaiZQ',
  },
});

export const AddressFactory = ({
  isEdit,
  notifyParent = noop,
  onSave = noop,
  isControlled = false,
  addressType = SHIP_TO_ADDRESS_TYPE,
  address,
  disabled = false,
  email = null,
  onChange = noop,
  pasteAddressRequest = noop,
  pasteAddressValue = '',
  hasError = false,
  autoCompleteRecipientName = false,
  onCustomerSelected = noop,
  customers = emptyArray,
  onBlur = noop,
  hiddenFields = emptyArray,
  requiredFields = emptyArray,
  showAddressLine3 = true,
  showChevron = true,
  wrapperClassName = '',
  hidePasteAddress = false,
  editAddressWrapperClassName = '',
  editWrapperClassName = '',
  addressValidationComponent,
  disableEditButton = false,
  addressWrapperClassName = '',
  salesOrderId = '',
  requireAll = false,
}) => {
  const [stateIsEdit, setStateIsEdit] = useState(isEdit !== undefined);
  const dispatch = useDispatch();
  const fulfillmentPlanIds = useSelector((state) =>
    Global.selectEntitiesFulfillmentPlanIdsBySalesOrderIds(state, {
      salesOrderIds: [salesOrderId],
    }),
  );
  const fulfillmentPlans = useSelector((state) => Global.selectEntitiesFulfillmentPlansByIds(state, { fulfillmentPlanIds }));
  // checks if any of the Fulfillment plans have a locked address if so then show the lock address component
  const isAddressLocked = fulfillmentPlans.some((fp) => fp.lockAddress);
  const _setStateOrNotify = useCallback(
    (isEditValue) => {
      if (isEdit === undefined) {
        setStateIsEdit(isEditValue);
        return;
      }
      notifyParent();
    },
    // oxlint-disable-next-line react-hooks/exhaustive-deps
    [isEdit, stateIsEdit, notifyParent],
  );
  const onEdit = useCallback(() => {
    setStateIsEdit(true);
  }, [setStateIsEdit]);
  const onAddressSave = useCallback(
    (newAddress, newEmail, autoCorrect) => {
      onSave(newAddress, newEmail, autoCorrect);
      _setStateOrNotify(false);
    },
    [_setStateOrNotify, onSave],
  );
  const onEditCancel = useCallback(() => {
    _setStateOrNotify(false);
  }, [_setStateOrNotify]);
  const inEditMode = useMemo(() => {
    if (isEdit === undefined) {
      return stateIsEdit;
    }
    return isEdit;
  }, [stateIsEdit, isEdit]);
  const onUnlock = useCallback(() => dispatch(unlockAddressRequest(fulfillmentPlanIds)), [dispatch, fulfillmentPlanIds]);

  if (isControlled) {
    return (
      <ControlledComponentAddressForm
        address={address}
        addressType={addressType}
        autoCompleteRecipientName={autoCompleteRecipientName}
        customers={customers}
        disabled={disabled}
        email={email}
        hasError={hasError}
        hiddenFields={hiddenFields}
        hidePasteAddress={hidePasteAddress}
        onBlur={onBlur}
        onChange={onChange}
        onCustomerSelected={onCustomerSelected}
        pasteAddressRequest={pasteAddressRequest}
        pasteAddressValue={pasteAddressValue}
        requireAll={requireAll}
        requiredFields={requiredFields}
        showAddressLine3={showAddressLine3}
        showChevron={showChevron}
        wrapperClassName={wrapperClassName}
      />
    );
  }

  if (inEditMode) {
    return (
      <EditAddressComponent
        address={address}
        addressWrapperClassName={editAddressWrapperClassName}
        email={email}
        onCancel={onEditCancel}
        onSave={onAddressSave}
        requireAll={requireAll}
        showAddressLine3={showAddressLine3}
        showChevron={showChevron}
        wrapperClassName={editWrapperClassName}
      />
    );
  }

  const showEditButton = isEdit === undefined;
  const message = disableEditButton ? messages.editAddressDisabled : messages.editAddress;

  return (
    <div className={wrapperClassName}>
      {showEditButton && (
        <Button className={style['float-right']} disabled={disableEditButton} onClick={onEdit} theme='link'>
          <FormattedMessage {...message} />
        </Button>
      )}
      <ReadOnlyAddress address={address} addressWrapperClassName={addressWrapperClassName} />
      {addressValidationComponent}
      {isAddressLocked && <UnlockAddress onUnlock={onUnlock} />}
    </div>
  );
};

AddressFactory.propTypes = {
  addressType: AddressType,
  onSave: PropTypes.func,
  address: PropTypes.object.isRequired,
  hidePasteAddress: PropTypes.bool,
  email: PropTypes.string,
  isEdit: PropTypes.bool,
  notifyParent: PropTypes.func,
  isControlled: PropTypes.bool,
  onChange: PropTypes.func,
  pasteAddressRequest: PropTypes.func,
  pasteAddressValue: PropTypes.string,
  hasError: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.shape({
      name: PropTypes.bool,
      company: PropTypes.bool,
      line1: PropTypes.bool,
      line2: PropTypes.bool,
      line3: PropTypes.bool,
      city: PropTypes.bool,
      state: PropTypes.bool,
      postalCode: PropTypes.bool,
      countryCode: PropTypes.bool,
      phone: PropTypes.bool,
    }),
  ]),
  autoCompleteRecipientName: PropTypes.bool,
  showAddressLine3: PropTypes.bool,
  onCustomerSelected: PropTypes.func,
  customers: PropTypes.array,
  onBlur: PropTypes.func,
  addressValidationComponent: PropTypes.node,
  disableEditButton: PropTypes.bool,
  hiddenFields: PropTypes.array,
  requiredFields: PropTypes.array,
  showChevron: PropTypes.bool,
  addressWrapperClassName: PropTypes.string,
  wrapperClassName: PropTypes.string,
  editWrapperClassName: PropTypes.string,
  editAddressWrapperClassName: PropTypes.string,
  disabled: PropTypes.bool,
  salesOrderId: PropTypes.string,
  requireAll: PropTypes.bool,
};
